import { AppDialogsService } from '@/app/services/app-dialogs.service';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ofType } from '@ces/sourced-action';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/model';
import { addItemsToList, addWatchList, loadWatchLists, removeItemsFromList, watchListAdded, watchListAddFailed } from '@watch-lists/state/feature/entities/watch-list/watch-list.actions';
import { WatchList } from 'egov-api';
import { BehaviorSubject, combineLatest, merge } from 'rxjs';
import { delay, filter, first, map, takeUntil } from 'rxjs/operators';
import { selectSortedWatchLists, selectWatchListsLoading } from '../../state/feature/entities/watch-list/watch-list.selectors';

@Component({
    selector: 'watch-item-dialog',
    template: `
        <app-progress-bar *ngIf="loading$ | async"></app-progress-bar>
        <egov-dialog-close-button></egov-dialog-close-button>
        <div class="prevent-overlap-with-close-button"></div>
        <div class="watch-parcel-dialog">
            <h2 mat-dialog-title>
                Watch Account
                <span class="account-identification unimportant-text">{{ humanId }}</span>
            </h2>
            <p>You can add items to watch lists to group, manage, and keep track of your taxes.</p>
            <p>You can see all the lists in your <a href (click)="$event.preventDefault(); goToWatchLists()">Watch Lists</a> page.</p>
            <mat-dialog-content>
                <ol class="toggleable-list">
                    <li>
                        <form #form="ngForm" [formGroup]="formGroup" (ngSubmit)="createNewWatchList($event)">
                            <input
                                type="text"
                                formControlName="name"
                                [placeholder]="fieldOnFocus ? 'Enter name for new list' : 'Create new list'"
                                maxlength="100"
                                required
                                (focus)="fieldOnFocus = true"
                                (blur)="fieldOnFocus = false"
                            />
                            <app-icon-button [tabindex]="-1" title="Create new Watch List" [spinner]="creatingWatchList$ | async" [disabled]="!form.value.name">
                                arrow_forward
                            </app-icon-button>
                        </form>
                    </li>
                    <li *ngFor="let list of lists$ | async; trackBy: listTrackBy">
                        <button mat-button (click)="toggleParcelInList(list)" (focus)="preventFocusInsideElement($event.currentTarget)">
                            <span class="label">{{ list.name }}</span>
                            <mat-slide-toggle tabindex="-1" [style.pointer-events]="'none'" [checked]="list.containsAccount" [disableRipple]="true"></mat-slide-toggle>
                        </button>
                    </li>
                </ol>
            </mat-dialog-content>
        </div>
    `,
    styleUrls: ['./watch-item-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchItemDialogComponent implements OnInit
{

    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly actions$: Actions,
        private readonly appDialogsService: AppDialogsService,
        private readonly router: Router,


        // Passed values

        protected dialogRef: MatDialogRef<WatchItemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) protected data: WatchItemDialogData,
    )
    {}


    // Initialization

    public ngOnInit()
    {
        setTimeout(() => this.store.dispatch(loadWatchLists('WatchParcelComponent .ngOninit()', { notifyErrors: true })), 0);
    }


    // IDs

    protected readonly id = this.data.id;
    protected readonly humanId = this.data.humanId;


    // Toggle parcel in list

    // When the dialog is first opened it shows the lists it's got in memory if it has any
    // and requests an update of the lists from the server to make sure nothing changed.
    //
    // This behavior is intended to allow users to open the dialog, mark a list and quickly
    // move on to something else without having to wait for all the lists to be loaded
    // each time.
    //
    // If a user checks or unchecks a list before the server responds with the update to
    // the list information, this recent change is overriden in the state by the data sent
    // from the server, which won't contain this latest update.
    //
    // In order to prevent this, we use the following two properties to keep track of what
    // was updated in this dialog, to make sure it's not overriden by the new state served
    // from the server.

    protected readonly checkedHere$ = new BehaviorSubject(new Set<string>());
    protected readonly uncheckedHere$ = new BehaviorSubject(new Set<string>());

    protected toggleParcelInList(list: WatchList): void
    {
        this.appDialogsService.signIn().subscribe(() =>
        {
            if (list.items?.find(item => item.id === this.data.id))
            {
                const uncheckedHere = this.uncheckedHere$.value;
                uncheckedHere.add(list.slug);
                this.uncheckedHere$.next(new Set(uncheckedHere));

                const checkedHere = this.checkedHere$.value;
                checkedHere.delete(list.slug);
                this.checkedHere$.next(new Set(checkedHere));

                this.store.dispatch(removeItemsFromList({
                    slug: list.slug,
                    items: [{ type: this.data.type, id: this.data.id }],
                    optimistic: true,
                    notifyErrors: true,
                }));
            }
            else
            {
                const checkedHere = this.checkedHere$.value;
                checkedHere.add(list.slug);
                this.checkedHere$.next(new Set(checkedHere));

                const uncheckedHere = this.uncheckedHere$.value;
                uncheckedHere.delete(list.slug);
                this.uncheckedHere$.next(new Set(uncheckedHere));

                this.store.dispatch(addItemsToList({
                    slug: list.slug,
                    items: [{ type: this.data.type, id: this.data.id }],
                    optimistic: true,
                    notifyErrors: true,
                }));
            }
        });
    }


    // New watch list

    protected fieldOnFocus = false;

    protected readonly formGroup: UntypedFormGroup = new UntypedFormGroup({
        name: new UntypedFormControl('')
    });

    protected get nameValue(): string { return this.formGroup.get('name')!.value; }

    protected creatingWatchList$ = new BehaviorSubject(false);

    protected createNewWatchList(e: Event)
    {
        e.preventDefault();

        this.appDialogsService.signIn().subscribe(() =>
        {
            this.creatingWatchList$.next(true);

            const name = this.formGroup.get('name')!.value;

            this.store.dispatch(addWatchList('WatchParcelComponent .createNewWAtchList()', {
                name,
                items: [{ type: this.data.type, id: this.data.id }],
                notifyErrors: true
            }));

            const added$ = this.actions$.pipe(ofType(watchListAdded), filter(a => a.list.name === name));
            const failed$ = this.actions$.pipe(ofType(watchListAddFailed), filter(a => a.name === name));

            merge(added$, failed$).pipe(first()).subscribe(() => this.creatingWatchList$.next(false));
            added$.pipe(first(), takeUntil(failed$)).subscribe(() => this.formGroup.get('name')!.setValue(''));
        });
    }


    // Lists

    protected readonly lists$ = combineLatest([
        this.store.select(selectSortedWatchLists()),
        this.checkedHere$,
        this.uncheckedHere$,
    ]).pipe(
        map(([lists, checkedHere, uncheckedHere]) => lists.map(list => (
        {
            ...list,
            containsAccount:
                (
                    !!list.items?.find(item => item.id === this.data.id)
                    ||
                    checkedHere.has(list.slug)
                )
                &&
                !uncheckedHere.has(list.slug),
        })))
    );
    protected readonly loading$ = this.store.select(selectWatchListsLoading);

    protected listTrackBy(index: number, list: WatchList) { return list.slug; }

    protected preventFocusInsideElement(button: HTMLButtonElement)
    {
        button.querySelectorAll('input').forEach(input => input.tabIndex = -1);
    }


    // Go to Watch Lists

    protected goToWatchLists()
    {
        this.dialogRef.close();
        this.dialogRef.afterClosed().pipe(first(), delay(400)).subscribe(() => { this.router.navigateByUrl('watch-lists'); });
    }

}

export interface WatchItemDialogData
{
    type: string
    id: string
    humanId: string
}
